import { useCallback } from 'react'

import { useStripe, useElements } from '@stripe/react-stripe-js'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

interface UseStripeCheckoutOptions {
	onSuccess?: () => void
	clientSecret?: string | null
}

export const useStripeCheckout = ({
	onSuccess,
	clientSecret,
}: UseStripeCheckoutOptions = {}) => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const queryClient = useQueryClient()
	const stripe = useStripe()
	const elements = useElements()

	// Helper function to get user-friendly error message based on Stripe error code
	const getStripeErrorMessage = useCallback(
		(error: any) => {
			// 3DS Authentication errors - Check main error first
			if (
				error.code === 'payment_intent_authentication_failure' ||
				error.code === 'setup_intent_authentication_failure'
			) {
				return (
					t('payment_method.authentication_failed') ||
					'Authentication failed. Please try again or use a different payment method.'
				)
			}

			// Check setup_intent.last_setup_error for more details
			if (
				error.setup_intent?.last_setup_error?.code ===
					'setup_intent_authentication_failure' ||
				error.payment_intent?.last_payment_error?.code ===
					'payment_intent_authentication_failure'
			) {
				return (
					t('payment_method.authentication_failed') ||
					'Authentication failed. Please try again or use a different payment method.'
				)
			}

			// Card declined errors
			if (error.decline_code === 'authentication_required') {
				return (
					t('payment_method.authentication_required') ||
					'Your bank requires authentication. Please use a different card or contact your bank.'
				)
			}

			// Other common errors
			if (error.code === 'card_declined') {
				return (
					t('payment_method.card_declined') ||
					'Your card was declined. Please try a different payment method.'
				)
			}

			if (error.code === 'expired_card') {
				return (
					t('payment_method.expired_card') ||
					'Your card has expired. Please use a different card.'
				)
			}

			if (error.code === 'incorrect_cvc') {
				return (
					t('payment_method.incorrect_cvc') ||
					'The security code is incorrect. Please check and try again.'
				)
			}

			if (error.code === 'processing_error') {
				return (
					t('payment_method.processing_error') ||
					'An error occurred while processing your card. Please try again.'
				)
			}

			// Don't use Stripe's generic message if it's the authentication one
			if (
				error.message ===
				'We are unable to authenticate your payment method. Please choose a different payment method and try again.'
			) {
				return (
					t('payment_method.save_error_description') ||
					'Unable to verify your payment method. Please try a different card or contact your bank for assistance.'
				)
			}

			// Return the original message if available, otherwise generic message
			return (
				error.message ||
				t('payment_method.save_error_description') ||
				'We are unable to authenticate your payment method. Please choose a different payment method and try again.'
			)
		},
		[t]
	)

	const confirmPayment = useCallback(async () => {
		if (!stripe || !elements) {
			showNotification({
				type: 'error',
				message: t('payment_method.save_error') || 'Error',
				description: 'Stripe is not initialized',
			})
			return {
				type: 'error' as const,
				error: { message: 'Stripe is not initialized' },
			}
		}

		if (!clientSecret) {
			showNotification({
				type: 'error',
				message: t('payment_method.save_error') || 'Error',
				description: 'Client secret is missing',
			})
			return {
				type: 'error' as const,
				error: { message: 'Client secret is missing' },
			}
		}

		try {
			// Check if it's a SetupIntent (seti_xxx) or PaymentIntent (pi_xxx)
			const isSetupIntent = clientSecret.startsWith('seti_')

			if (isSetupIntent) {
				// For SetupIntents, use confirmSetup()
				const { error: submitError, setupIntent } = await stripe.confirmSetup({
					elements,
					confirmParams: {
						return_url: window.location.href,
					},
					redirect: 'if_required',
				})

				if (submitError) {
					showNotification({
						type: 'error',
						message: t('payment_method.save_error') || 'Error',
						description: getStripeErrorMessage(submitError),
					})
					return { type: 'error' as const, error: submitError }
				}

				if (!setupIntent) {
					showNotification({
						type: 'error',
						message: t('payment_method.save_error') || 'Error',
						description: 'Setup intent not found',
					})
					return {
						type: 'error' as const,
						error: { message: 'Setup intent not found' },
					}
				}

				// Setup confirmed successfully - webhook will handle the rest
			} else {
				// For PaymentIntents, use confirmPayment()
				const { error: submitError, paymentIntent } =
					await stripe.confirmPayment({
						elements,
						confirmParams: {
							return_url: window.location.href,
						},
						redirect: 'if_required',
					})

				if (submitError) {
					showNotification({
						type: 'error',
						message: t('payment_method.save_error') || 'Error',
						description: getStripeErrorMessage(submitError),
					})
					return { type: 'error' as const, error: submitError }
				}

				if (!paymentIntent) {
					showNotification({
						type: 'error',
						message: t('payment_method.save_error') || 'Error',
						description: 'Payment intent not found',
					})
					return {
						type: 'error' as const,
						error: { message: 'Payment intent not found' },
					}
				}

				// Payment confirmed successfully - webhook will handle the rest
			}

			// Stripe confirmed successfully - the webhook will handle saving the payment method
			// Invalidate queries so they'll refetch when needed
			queryClient.invalidateQueries({ queryKey: ['account'] })
			queryClient.invalidateQueries({ queryKey: ['availablePaymentMethods'] })
			queryClient.invalidateQueries({ queryKey: ['paymentTokens'] })

			showNotification({
				type: 'success',
				message:
					t('payment_method.save_success') ||
					'Payment method saved successfully',
			})

			// Call onSuccess immediately - let the parent component handle verification
			onSuccess?.()

			return { type: 'success' as const }
		} catch (error: any) {
			showNotification({
				type: 'error',
				message: t('payment_method.save_error') || 'Error',
				description:
					extractErrorMessage(error) ||
					t('payment_method.save_error_description') ||
					'Failed to save payment method',
			})
			return {
				type: 'error' as const,
				error: { message: error?.message || 'Failed to save payment method' },
			}
		}
	}, [
		stripe,
		elements,
		clientSecret,
		showNotification,
		t,
		queryClient,
		onSuccess,
		getStripeErrorMessage,
	])

	return {
		confirmPayment,
		isLoading: !stripe || !elements,
		isReady: !!stripe && !!elements,
	}
}
